import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { message, Spin } from "antd";
import { useLazyQuery } from "@apollo/client";
import { Container } from "./style";
import { setLoginInfo } from "../../store/features/UserInfo";
import { exchangeCodeForTokens } from "../../utils/cognitoOAuth";
import { GET_CURRENT_USER_ID, useCompleteLogin } from "./loginQueries";

// Cognito wraps any Lambda trigger's thrown error as "<TriggerName> failed
// with error <message>." (e.g. "PreSignUp failed with error ..."), and
// appends its own trailing period on top of whatever the Lambda's message
// already ended with - strip both so only our own message text shows.
function cleanCognitoErrorDescription(text) {
  if (!text) return text;
  return text
    .replace(/^[A-Za-z]+ failed with error /i, "")
    .trim()
    .replace(/\.\.$/, ".");
}

// Lands here after Cognito Hosted UI finishes the Google sign-in redirect
// (see src/utils/cognitoOAuth.js). Exchanges the auth code for real Cognito
// tokens, then runs the same post-login sequence as the password form.
export default function AuthCallback() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [getCurrentUserId] = useLazyQuery(GET_CURRENT_USER_ID);
  const handleAfterLogin = useCompleteLogin();
  const [errorText, setErrorText] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const error = params.get("error");
      const errorDescription = params.get("error_description");
      const code = params.get("code");
      const state = params.get("state");

      if (error) {
        console.error("Cognito redirected back with an error:", {
          error,
          errorDescription,
          fullUrl: window.location.href,
        });
        setErrorText(
          cleanCognitoErrorDescription(errorDescription) ||
            `Google sign-in failed (${error}).`
        );
        return;
      }
      if (!code) {
        setErrorText("Missing sign-in code. Please try again.");
        return;
      }

      try {
        const tokens = await exchangeCodeForTokens(code, state);
        dispatch(setLoginInfo(tokens));

        const res = await getCurrentUserId();
        const userId = res.data?.getCurrentUser?._id;
        if (!userId) {
          setErrorText("Could not sign you in. Please try again.");
          return;
        }
        await handleAfterLogin(userId);
      } catch (err) {
        console.error("Google sign-in error:", err);
        setErrorText(err.message || "Google sign-in failed.");
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (errorText) {
      message.error(errorText);
      navigate("/Login", { replace: true });
    }
  }, [errorText, navigate]);

  return (
    <Container>
      <div className="loginPage" style={{ textAlign: "center" }}>
        <Spin size="large" />
        <p>Signing you in...</p>
      </div>
    </Container>
  );
}
